import React, { useState, useEffect } from 'react';
import API from '../services/api';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  InputNumber,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Alert,
  Popconfirm,
  Badge,
  Tooltip,
  Spin,
  Select,
  Tabs
} from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  HistoryOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import './AdminAutoProtection.css';

const { Title, Text } = Typography;

// 日志类型映射配置
const LOG_TYPE_MAPPING = {
  // 攻击检测
  'ATTACK_DETECTED': {
    text: '检测到攻击',
    icon: '🚨',
    color: '#f5222d',
    severity: 'high',
    category: 'attack'
  },
  'ATTACK_ENDED': {
    text: '攻击结束',
    icon: '✅',
    color: '#52c41a',
    severity: 'medium',
    category: 'attack'
  },
  
  // 防护策略
  'PROTECTION_STRATEGIES_TRIGGERED': {
    text: '防护策略已触发',
    icon: '🛡️',
    color: '#1890ff',
    severity: 'medium',
    category: 'protection'
  },
  'PROTECTION_STRATEGIES_ALL_FAILED': {
    text: '所有防护策略触发失败',
    icon: '❌',
    color: '#f5222d',
    severity: 'high',
    category: 'protection'
  },
  'PROTECTION_STRATEGIES_ERROR': {
    text: '防护策略触发异常',
    icon: '⚠️',
    color: '#fa8c16',
    severity: 'high',
    category: 'protection'
  },
  'PROTECTION_STRATEGIES_REMOVED': {
    text: '防护策略已移除',
    icon: '🔓',
    color: '#8c8c8c',
    severity: 'low',
    category: 'protection'
  },
  'PROTECTION_STRATEGIES_REMOVE_FAILED': {
    text: '防护策略移除失败',
    icon: '❌',
    color: '#fa8c16',
    severity: 'medium',
    category: 'protection'
  },
  'PROTECTION_STRATEGIES_REMOVE_CANCELLED': {
    text: '防护策略移除已取消',
    icon: '⚠️',
    color: '#4299e1',
    severity: 'medium',
    category: 'protection'
  },
  
  // UDP策略
  'UDP_STRATEGY_ENABLED': {
    text: 'UDP封禁策略已启用',
    icon: '🔒',
    color: '#1890ff',
    severity: 'medium',
    category: 'udp'
  },
  'UDP_STRATEGY_EXISTS': {
    text: 'UDP封禁策略已存在',
    icon: 'ℹ️',
    color: '#8c8c8c',
    severity: 'low',
    category: 'udp'
  },
  'UDP_STRATEGY_FAILED': {
    text: 'UDP封禁策略启用失败',
    icon: '❌',
    color: '#fa8c16',
    severity: 'medium',
    category: 'udp'
  },
  'UDP_STRATEGY_ERROR': {
    text: 'UDP封禁策略启用异常',
    icon: '⚠️',
    color: '#f5222d',
    severity: 'high',
    category: 'udp'
  },
  
  // 海外策略
  'OVERSEAS_STRATEGY_ENABLED': {
    text: '海外封禁策略已启用',
    icon: '🌍',
    color: '#1890ff',
    severity: 'medium',
    category: 'overseas'
  },
  'OVERSEAS_STRATEGY_EXISTS': {
    text: '海外封禁策略已存在',
    icon: 'ℹ️',
    color: '#8c8c8c',
    severity: 'low',
    category: 'overseas'
  },
  'OVERSEAS_STRATEGY_FAILED': {
    text: '海外封禁策略启用失败',
    icon: '❌',
    color: '#fa8c16',
    severity: 'medium',
    category: 'overseas'
  },
  'OVERSEAS_STRATEGY_ERROR': {
    text: '海外封禁策略启用异常',
    icon: '⚠️',
    color: '#f5222d',
    severity: 'high',
    category: 'overseas'
  },
  
  // 用户操作
  'IP_ADDED_BY_USER': {
    text: '用户启用自动防护',
    icon: '👤',
    color: '#52c41a',
    severity: 'low',
    category: 'user'
  },
  'IP_REMOVED_BY_USER': {
    text: '用户关闭自动防护',
    icon: '👤',
    color: '#8c8c8c',
    severity: 'low',
    category: 'user'
  },
  'CONFIG_UPDATED_BY_USER': {
    text: '用户更新防护配置',
    icon: '👤',
    color: '#1890ff',
    severity: 'low',
    category: 'user'
  },
  'MANUAL_CHECK_BY_USER': {
    text: '用户手动检查状态',
    icon: '👤',
    color: '#1890ff',
    severity: 'low',
    category: 'user'
  },
  
  // 管理员操作
  'IP_ADDED': {
    text: '管理员启用自动防护',
    icon: '👨‍💼',
    color: '#52c41a',
    severity: 'low',
    category: 'admin'
  },
  'IP_REMOVED': {
    text: '管理员关闭自动防护',
    icon: '👨‍💼',
    color: '#8c8c8c',
    severity: 'low',
    category: 'admin'
  },
  'CONFIG_UPDATED': {
    text: '管理员更新防护配置',
    icon: '👨‍💼',
    color: '#1890ff',
    severity: 'low',
    category: 'admin'
  },
  'MANUAL_PROTECTION_REMOVED': {
    text: '管理员手动移除防护策略',
    icon: '👨‍💼',
    color: '#fa8c16',
    severity: 'medium',
    category: 'admin'
  }
};

// 获取日志类型信息
const getLogTypeInfo = (logType) => {
  return LOG_TYPE_MAPPING[logType] || {
    text: logType,
    icon: '📝',
    color: '#8c8c8c',
    severity: 'low',
    category: 'unknown'
  };
};

const AdminAutoProtection = () => {
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [protectedIPs, setProtectedIPs] = useState([]);
  const [filteredIPs, setFilteredIPs] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedIPDetail, setSelectedIPDetail] = useState(null);
  const [editingIPData, setEditingIPData] = useState(null);
  const [operationLogs, setOperationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [attackFrequencyStats, setAttackFrequencyStats] = useState(null);
  const [delayPrediction, setDelayPrediction] = useState(null);
  const [delayCalculationHistory, setDelayCalculationHistory] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [analysisIPData, setAnalysisIPData] = useState(null);
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [logsIPData, setLogsIPData] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // 获取系统状态
  const fetchSystemStatus = async () => {
    try {
      const response = await API.get('/api/admin/auto-protection/status');
      const result = response.data;
      
      if (result.code === 0) {
        setSystemStatus(result.data);
      } else {
        message.error(result.msg || result.message || '获取系统状态失败');
      }
    } catch (error) {
      console.error('获取系统状态失败:', error);
      message.error('获取系统状态失败');
    }
  };

  // 获取受保护IP列表
  const fetchProtectedIPs = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/admin/auto-protection/protected-ips');
      const result = response.data;
      
      if (result.code === 0) {
        const ipList = result.data.protectedIPs || [];
        
        // 按创建时间倒序排序
        const sortedIPs = ipList.sort((a, b) => {
          const timeA = a.config?.createdAt ? new Date(a.config.createdAt).getTime() : 0;
          const timeB = b.config?.createdAt ? new Date(b.config.createdAt).getTime() : 0;
          return timeB - timeA; // 倒序排序
        });
        
        setProtectedIPs(sortedIPs);
        setFilteredIPs(sortedIPs);
      } else {
        message.error(result.msg || result.message || '获取IP列表失败');
      }
    } catch (error) {
      console.error('获取IP列表失败:', error);
      message.error('获取IP列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 切换系统状态
  const toggleSystem = async (action) => {
    try {
      const response = await API.post('/api/admin/auto-protection/toggle', { action });
      const result = response.data;
      
      if (result.code === 0) {
        message.success(result.msg);
        fetchSystemStatus();
      } else {
        message.error(result.msg || result.message || '操作失败');
      }
    } catch (error) {
      console.error('切换系统状态失败:', error);
      message.error('操作失败');
    }
  };

  // 添加IP
  const handleAddIP = async (values) => {
    try {
      const response = await API.post('/api/admin/auto-protection/add-ip', values);
      const result = response.data;
      
      if (result.code === 0) {
        message.success('IP添加成功');
        setAddModalVisible(false);
        form.resetFields();
        fetchProtectedIPs();
        fetchSystemStatus();
      } else {
        message.error(result.msg || result.message || '添加失败');
      }
    } catch (error) {
      console.error('添加IP失败:', error);
      message.error('添加失败');
    }
  };

  // 删除IP
  const handleRemoveIP = async (ip) => {
    try {
      const response = await API.delete('/api/admin/auto-protection/remove-ip', {
        data: { ip }
      });
      const result = response.data;
      
      if (result.code === 0) {
        message.success('IP删除成功');
        fetchProtectedIPs();
        fetchSystemStatus();
      } else {
        message.error(result.msg || result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除IP失败:', error);
      message.error('删除失败');
    }
  };

  // 移除防护策略
  const handleRemoveProtection = async (ip) => {
    try {
      const response = await API.post('/api/admin/auto-protection/remove-protection', { ip });
      const result = response.data;
      
      if (result.code === 0) {
        message.success('防护策略移除成功');
        fetchProtectedIPs();
      } else {
        message.error(result.msg || result.message || '移除失败');
      }
    } catch (error) {
      console.error('移除防护策略失败:', error);
      message.error('移除失败');
    }
  };

  // 编辑IP配置
  const handleEditIP = async (values) => {
    try {
      const response = await API.put('/api/admin/auto-protection/edit-ip', {
        ip: editingIPData.ip,
        ...values
      });
      const result = response.data;
      
      if (result.code === 0) {
        message.success('IP配置更新成功');
        setEditModalVisible(false);
        editForm.resetFields();
        setEditingIPData(null);
        fetchProtectedIPs();
        fetchSystemStatus();
      } else {
        message.error(result.msg || result.message || '更新失败');
      }
    } catch (error) {
      console.error('更新IP配置失败:', error);
      message.error('更新失败');
    }
  };

  // 打开编辑模态框
  const openEditModal = (ipData) => {
    setEditingIPData(ipData);
    const delayMode = ipData.config.delayMode || 'manual';
    editForm.setFieldsValue({
      enableUdpBlock: ipData.config.enableUdpBlock,
      enableOverseasBlock: ipData.config.enableOverseasBlock,
      delayMode: delayMode,
      attackEndDelay: delayMode === 'manual' ? ipData.config.attackEndDelay : undefined,
      baseDelay: delayMode === 'auto' ? ipData.config.baseDelay : undefined,
      isActive: ipData.config.isActive
    });
    setEditModalVisible(true);
  };

  // 查看详情
  const handleViewDetail = (ipData) => {
    setSelectedIPDetail(ipData);
    setDetailModalVisible(true);
  };

  // 打开智能分析
  const handleOpenAnalysis = (ipData) => {
    setAnalysisIPData(ipData);
    setAnalysisModalVisible(true);
    // 加载智能分析数据
    loadAnalysisData(ipData.ip);
  };

  // 打开操作日志
  const handleOpenLogs = (ipData) => {
    setLogsIPData(ipData);
    setLogsModalVisible(true);
    // 加载操作日志
    fetchOperationLogs(ipData.ip);
  };

  // 获取操作日志
  const fetchOperationLogs = async (ip) => {
    setLogsLoading(true);
    try {
      const response = await API.get(`/api/admin/auto-protection/operation-logs/${ip}?limit=50`);
      const result = response.data;
      
      if (result.code === 0) {
        const logs = result.data.logs || [];
        // 确保每个日志项都是有效的对象，并且包含必要的字段
        const validLogs = logs.filter(log => 
          log && 
          typeof log === 'object' && 
          log.action && 
          log.description
        );
        setOperationLogs(validLogs);
      } else {
        console.error('获取日志失败:', result.msg);
        setOperationLogs([]);
      }
    } catch (error) {
      console.error('获取操作日志失败:', error);
      setOperationLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // 获取攻击频率统计
  const fetchAttackFrequencyStats = async (ip) => {
    try {
      const response = await API.get(`/api/admin/auto-protection/attack-frequency-stats/${ip}`);
      const result = response.data;
      
      if (result.code === 0) {
        setAttackFrequencyStats(result.data);
      } else {
        console.error('获取攻击频率统计失败:', result.msg);
        setAttackFrequencyStats(null);
      }
    } catch (error) {
      console.error('获取攻击频率统计失败:', error);
      setAttackFrequencyStats(null);
    }
  };

  // 预测延迟时间
  const predictDelayTime = async (ip, baseDelay = 300) => {
    try {
      const response = await API.post('/api/admin/auto-protection/predict-delay-time', {
        ip,
        baseDelay
      });
      const result = response.data;
      
      if (result.code === 0) {
        setDelayPrediction(result.data);
        message.success('延迟时间预测完成');
      } else {
        message.error(result.msg || '延迟时间预测失败');
      }
    } catch (error) {
      console.error('延迟时间预测失败:', error);
      message.error('延迟时间预测失败');
    }
  };

  // 获取延迟计算历史
  const fetchDelayCalculationHistory = async (ip) => {
    try {
      const response = await API.get(`/api/admin/auto-protection/delay-calculation-history/${ip}`);
      const result = response.data;
      
      if (result.code === 0) {
        setDelayCalculationHistory(result.data);
      } else {
        console.error('获取延迟计算历史失败:', result.msg);
        setDelayCalculationHistory(null);
      }
    } catch (error) {
      console.error('获取延迟计算历史失败:', error);
      setDelayCalculationHistory(null);
    }
  };

  // 加载智能分析数据
  const loadAnalysisData = async (ip) => {
    setAnalysisLoading(true);
    try {
      await Promise.all([
        fetchAttackFrequencyStats(ip),
        fetchDelayCalculationHistory(ip)
      ]);
    } catch (error) {
      console.error('加载智能分析数据失败:', error);
    } finally {
      setAnalysisLoading(false);
    }
  };

  // 搜索功能
  const handleSearch = (value) => {
    if (!value) {
      setFilteredIPs(protectedIPs);
    } else {
      const filtered = protectedIPs.filter(item => 
        item.ip.toLowerCase().includes(value.toLowerCase())
      );
      // 搜索结果也按创建时间倒序排序
      const sortedFiltered = filtered.sort((a, b) => {
        const timeA = a.config?.createdAt ? new Date(a.config.createdAt).getTime() : 0;
        const timeB = b.config?.createdAt ? new Date(b.config.createdAt).getTime() : 0;
        return timeB - timeA;
      });
      setFilteredIPs(sortedFiltered);
    }
  };

  // 状态标签渲染
  const renderStatusTag = (status) => {
    const statusConfig = {
      'ongoing': { color: 'red', text: '攻击中' },
      'ended': { color: 'orange', text: '攻击结束' },
      'none': { color: 'green', text: '正常' }
    };
    const config = statusConfig[status] || { color: 'default', text: '未知' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 表格列定义
  const columns = [
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      fixed: 'left',
      width: 140,
      render: (ip) => <Text code>{ip}</Text>
    },
    {
      title: '启用状态',
      key: 'isActive',
      width: 90,
      render: (_, record) => (
        <Badge 
          status={record.config.isActive ? 'processing' : 'default'} 
          text={record.config.isActive ? '启用' : '禁用'} 
        />
      )
    },
    {
      title: '攻击状态',
      key: 'currentStatus',
      width: 90,
      render: (_, record) => renderStatusTag(record.config.currentStatus)
    },
    {
      title: '防护策略',
      key: 'protection',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.config.enableUdpBlock && <Tag color="blue">UDP封禁</Tag>}
          {record.config.enableOverseasBlock && <Tag color="purple">海外封禁</Tag>}
        </Space>
      )
    },
    {
      title: '当前防护',
      key: 'currentProtection',
      width: 120,
      render: (_, record) => {
        if (!record.summary.currentlyProtected) {
          return <Text type="secondary">无</Text>;
        }
        return (
          <Space direction="vertical" size="small">
            {record.summary.currentlyProtected.udp && <Tag color="red">UDP已封禁</Tag>}
            {record.summary.currentlyProtected.overseas && <Tag color="red">海外已封禁</Tag>}
          </Space>
        );
      }
    },
    {
      title: '延迟配置',
      key: 'delayConfig',
      width: 120,
      render: (_, record) => {
        if (!record || !record.config) return '-';
        const config = record.config;
        const delayMode = config.delayMode || 'manual';
        
        if (delayMode === 'auto') {
          const baseDelay = config.baseDelay || 300;
          const currentDelay = config.currentCalculatedDelay || baseDelay;
          return (
            <div>
              <Tag color="cyan" size="small">自动延迟</Tag>
              <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                基础: {Math.floor(baseDelay / 60)}分钟
                {currentDelay !== baseDelay && (
                  <div>当前: {Math.floor(currentDelay / 60)}分钟</div>
                )}
              </div>
            </div>
          );
        } else {
          const delay = config.attackEndDelay;
        const minutes = Math.floor(delay / 60);
        const seconds = delay % 60;
          const timeText = seconds === 0 ? `${minutes}分钟` : `${minutes}分${seconds}秒`;
          return (
            <div>
              <Tag color="blue" size="small">手动延迟</Tag>
              <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                {timeText}
              </div>
            </div>
          );
        }
      }
    },
    {
      title: '最后检查',
      key: 'lastCheck',
      width: 160,
      render: (_, record) => (
        <Text type="secondary">
          {record.summary.lastCheckFormatted || '未检查'}
        </Text>
      )
    },
    {
      title: '创建时间',
      key: 'createdAt',
      width: 160,
      render: (_, record) => (
        <Text type="secondary">
          {record.summary.createdAtFormatted}
        </Text>
      )
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="编辑配置">
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="智能分析">
            <Button 
              type="link" 
              size="small" 
              icon={<SafetyOutlined />}
              onClick={() => handleOpenAnalysis(record)}
            />
          </Tooltip>
          <Tooltip title="操作日志">
            <Button 
              type="link" 
              size="small" 
              icon={<HistoryOutlined />}
              onClick={() => handleOpenLogs(record)}
            />
          </Tooltip>
          {record.summary.currentlyProtected && (
            <Tooltip title="移除防护">
              <Popconfirm
                title="确定要移除当前防护策略吗？"
                onConfirm={() => handleRemoveProtection(record.ip)}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  type="link" 
                  size="small" 
                  danger
                  icon={<StopOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          )}
          <Tooltip title="删除IP">
            <Popconfirm
              title="确定要从自动防护列表删除此IP吗？"
              onConfirm={() => handleRemoveIP(record.ip)}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                type="link" 
                size="small" 
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchSystemStatus();
    fetchProtectedIPs();
    
    // 设置定时刷新
    const interval = setInterval(() => {
      fetchSystemStatus();
      fetchProtectedIPs();
    }, 30000); // 每30秒刷新一次
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="admin-auto-protection">
      <div className="page-header">
        <Title level={2}>
          <SafetyOutlined /> 自动防护系统管理
        </Title>
        <Text type="secondary">管理和监控自动防护系统的运行状态</Text>
      </div>

      {/* 系统状态卡片 */}
      <Row gutter={[16, 16]} className="status-cards">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>系统状态</div>
                <div style={{ 
                  fontSize: 24, 
                  fontWeight: 500,
                  color: systemStatus?.system?.isRunning ? '#3f8600' : '#cf1322',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  {systemStatus?.system?.isRunning ? 
                    <PlayCircleOutlined /> : <PauseCircleOutlined />
                  }
                  {systemStatus?.system?.isRunning ? "运行中" : "已停止"}
                </div>
              </div>
              <Button 
                type={systemStatus?.system?.isRunning ? "default" : "primary"}
                danger={systemStatus?.system?.isRunning}
                icon={systemStatus?.system?.isRunning ? 
                  <PauseCircleOutlined /> : <PlayCircleOutlined />
                }
                onClick={() => toggleSystem(systemStatus?.system?.isRunning ? 'stop' : 'start')}
              >
                {systemStatus?.system?.isRunning ? "停止" : "启动"}
              </Button>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="受保护IP总数" 
              value={systemStatus?.statistics?.total || 0}
              prefix={<SafetyOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="启用中" 
              value={systemStatus?.statistics?.active || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic 
              title="攻击中" 
              value={systemStatus?.statistics?.underAttack || 0}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 活跃定时器信息 */}
      {systemStatus?.activeTimers && systemStatus.activeTimers.length > 0 && (
        <Card 
          title="活跃定时器" 
          size="small" 
          style={{ marginBottom: 16 }}
        >
          <Text type="secondary">
            当前有 {systemStatus.activeTimers.length} 个活跃的监控定时器
          </Text>
        </Card>
      )}

      {/* IP列表 */}
      <Card 
        title="受保护IP列表"
        extra={
          <Space>
            <Input.Search
              placeholder="搜索IP地址"
              allowClear
              style={{ width: 250 }}
              onSearch={handleSearch}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onChange={(e) => {
                // 当输入框被清空时，重置过滤结果
                if (!e.target.value) {
                  handleSearch('');
                }
              }}
            />
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => {
                fetchSystemStatus();
                fetchProtectedIPs();
              }}
            >
              刷新
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setAddModalVisible(true)}
            >
              添加IP
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredIPs}
          rowKey="ip"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
        />
      </Card>

      {/* 添加IP模态框 */}
      <Modal
        title="添加IP到自动防护列表"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
        centered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddIP}
          initialValues={{
            enableUdpBlock: true,
            enableOverseasBlock: false,
            delayMode: 'manual',
            attackEndDelay: 600,
            baseDelay: 300
          }}
        >
          <Alert
            message="提示"
            description="至少需要启用一种防护策略。系统将每10秒检查一次该IP的攻击状态并自动触发防护策略。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item
            label="IP地址"
            name="ip"
            rules={[
              { required: true, message: '请输入IP地址' },
              { pattern: /^(\d{1,3}\.){3}\d{1,3}$/, message: '请输入有效的IP地址' }
            ]}
          >
            <Input placeholder="例如：192.168.1.1" />
          </Form.Item>

          <Form.Item
            label="UDP封禁"
            name="enableUdpBlock"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="海外封禁"
            name="enableOverseasBlock"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="延迟模式"
            name="delayMode"
            rules={[{ required: true, message: '请选择延迟模式' }]}
          >
            <Select
              placeholder="请选择延迟模式"
              onChange={(value) => {
                // 当延迟模式改变时，重置相关字段
                if (value === 'manual') {
                  form.setFieldsValue({ baseDelay: undefined });
                } else {
                  form.setFieldsValue({ attackEndDelay: undefined });
                }
              }}
            >
              <Select.Option value="manual">手动延迟</Select.Option>
              <Select.Option value="auto">自动延迟</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item dependencies={['delayMode']} noStyle>
            {({ getFieldValue }) => {
              const delayMode = getFieldValue('delayMode');
              
              if (delayMode === 'manual') {
                return (
                  <Form.Item
                    label="固定延迟时间"
            name="attackEndDelay"
            rules={[
              { required: true, message: '请输入延迟时间' },
              { type: 'number', min: 60, max: 3600, message: '延迟时间应在60-3600秒之间' }
            ]}
                    extra="手动模式：使用固定的延迟时间，攻击结束后等待指定时间再移除防护"
                  >
                    <InputNumber
                      min={60}
                      max={3600}
                      style={{ width: '100%' }}
                      addonAfter="秒"
                      placeholder="600"
                    />
                  </Form.Item>
                );
              } else if (delayMode === 'auto') {
                return (
                  <Form.Item
                    label="基础延迟时间"
                    name="baseDelay"
                    rules={[
                      { required: true, message: '请输入基础延迟时间' },
                      { type: 'number', min: 60, max: 3600, message: '基础延迟时间应在60-3600秒之间' }
                    ]}
                    extra="自动模式：系统将根据攻击频率动态调整延迟时间，此处设置的是基础延迟时间"
          >
            <InputNumber
              min={60}
              max={3600}
              style={{ width: '100%' }}
              addonAfter="秒"
              placeholder="300"
            />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setAddModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑IP配置模态框 */}
      <Modal
        title={`编辑IP配置 - ${editingIPData?.ip}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setEditingIPData(null);
        }}
        footer={null}
        width={600}
        centered
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditIP}
        >
          <Alert
            message="提示"
            description="至少需要启用一种防护策略。修改配置后将立即生效。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item
            label="启用状态"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="UDP封禁"
            name="enableUdpBlock"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="海外封禁"
            name="enableOverseasBlock"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="延迟模式"
            name="delayMode"
            rules={[{ required: true, message: '请选择延迟模式' }]}
          >
            <Select
              placeholder="请选择延迟模式"
              onChange={(value) => {
                // 当延迟模式改变时，重置相关字段
                if (value === 'manual') {
                  editForm.setFieldsValue({ baseDelay: undefined });
                } else {
                  editForm.setFieldsValue({ attackEndDelay: undefined });
                }
              }}
            >
              <Select.Option value="manual">手动延迟</Select.Option>
              <Select.Option value="auto">自动延迟</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item dependencies={['delayMode']} noStyle>
            {({ getFieldValue }) => {
              const delayMode = getFieldValue('delayMode');
              
              if (delayMode === 'manual') {
                return (
                  <Form.Item
                    label="固定延迟时间"
            name="attackEndDelay"
            rules={[
              { required: true, message: '请输入延迟时间' },
              { type: 'number', min: 60, max: 3600, message: '延迟时间应在60-3600秒之间' }
            ]}
                    extra="手动模式：使用固定的延迟时间，攻击结束后等待指定时间再移除防护"
          >
            <InputNumber
              min={60}
              max={3600}
              style={{ width: '100%' }}
              addonAfter="秒"
              placeholder="600"
            />
                  </Form.Item>
                );
              } else if (delayMode === 'auto') {
                return (
                  <Form.Item
                    label="基础延迟时间"
                    name="baseDelay"
                    rules={[
                      { required: true, message: '请输入基础延迟时间' },
                      { type: 'number', min: 60, max: 3600, message: '基础延迟时间应在60-3600秒之间' }
                    ]}
                    extra="自动模式：系统将根据攻击频率动态调整延迟时间，此处设置的是基础延迟时间"
                  >
                    <InputNumber
                      min={60}
                      max={3600}
                      style={{ width: '100%' }}
                      addonAfter="秒"
                      placeholder="300"
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
                setEditingIPData(null);
              }}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyOutlined />
            <span>IP详情 - </span>
            <Text code style={{ fontSize: 16 }}>{selectedIPDetail?.ip}</Text>
          </div>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedIPDetail(null);
        }}
        footer={null}
        width={900}
        centered
      >
        {selectedIPDetail && (
          <div className="ip-detail">
            {/* 状态信息 */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Card title="系统配置状态" size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span>自动防护</span>
                    <Badge 
                      status={selectedIPDetail.config.isActive ? 'processing' : 'default'} 
                      text={
                        <span style={{ fontSize: 14, fontWeight: 500 }}>
                          {selectedIPDetail.config.isActive ? '启用' : '禁用'}
                        </span>
                      }
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span>延迟模式</span>
                    <Tag color={selectedIPDetail.config.delayMode === 'auto' ? 'cyan' : 'blue'}>
                      {selectedIPDetail.config.delayMode === 'auto' ? '自动延迟' : '手动延迟'}
                    </Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span>延迟时间</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>
                      {(() => {
                        const config = selectedIPDetail.config;
                        if (config.delayMode === 'auto') {
                          const baseDelay = config.baseDelay || 300;
                          const currentDelay = config.currentCalculatedDelay || baseDelay;
                          return (
                            <div style={{ textAlign: 'right' }}>
                              <div>基础: {Math.floor(baseDelay / 60)}分钟</div>
                              {currentDelay !== baseDelay && (
                                <div style={{ color: '#1890ff', fontSize: '12px' }}>
                                  当前: {Math.floor(currentDelay / 60)}分钟
                                </div>
                              )}
                            </div>
                          );
                        } else {
                          const delay = config.attackEndDelay;
                          const minutes = Math.floor(delay / 60);
                          const seconds = delay % 60;
                          return seconds === 0 ? `${minutes}分钟` : `${minutes}分${seconds}秒`;
                        }
                      })()}
                    </span>
                  </div>
                  
                </Card>
              </Col>
              <Col span={12}>
                <Card title="实时攻击状态" size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span>攻击状态</span>
                    <div>{renderStatusTag(selectedIPDetail.config.currentStatus)}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span>最后检查</span>
                    <span style={{ fontSize: 12 }}>
                      {selectedIPDetail.summary.lastCheckFormatted || '未检查'}
                    </span>
                  </div>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {/* 防护策略配置 */}
              <Col span={12}>
                <Card title="防护策略配置" size="small">
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>UDP封禁</span>
                      <Tag color={selectedIPDetail.config.enableUdpBlock ? 'green' : 'default'}>
                        {selectedIPDetail.config.enableUdpBlock ? '启用' : '禁用'}
                      </Tag>
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>海外封禁</span>
                      <Tag color={selectedIPDetail.config.enableOverseasBlock ? 'green' : 'default'}>
                        {selectedIPDetail.config.enableOverseasBlock ? '启用' : '禁用'}
                      </Tag>
                    </div>
                  </div>

                </Card>
              </Col>

              {/* 当前防护状态 */}
              <Col span={12}>
                <Card title="当前防护状态" size="small">
                  {selectedIPDetail.summary.currentlyProtected ? (
                    <>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>UDP封禁状态</span>
                          <Tag color={selectedIPDetail.summary.currentlyProtected.udp ? 'red' : 'green'}>
                            {selectedIPDetail.summary.currentlyProtected.udp ? '已封禁' : '未封禁'}
                          </Tag>
                        </div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>海外封禁状态</span>
                          <Tag color={selectedIPDetail.summary.currentlyProtected.overseas ? 'red' : 'green'}>
                            {selectedIPDetail.summary.currentlyProtected.overseas ? '已封禁' : '未封禁'}
                          </Tag>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                      <Text type="secondary">当前无激活的防护策略</Text>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            {/* 详细状态信息 */}
            <div style={{ marginTop: 16 }}>
              <Card title="详细状态信息" size="small">
                {selectedIPDetail.status ? (
                    <div style={{ 
                      background: '#f8f9fa', 
                      border: '1px solid #e9ecef',
                      borderRadius: 4, 
                      padding: 12,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}>
                      <pre style={{ 
                        margin: 0, 
                        fontSize: 12, 
                        lineHeight: 1.4,
                        color: '#495057'
                      }}>
                        {JSON.stringify(selectedIPDetail.status, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                      <Text type="secondary">暂无详细状态信息</Text>
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}
      </Modal>

      {/* 智能分析模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyOutlined />
            <span>智能分析 - </span>
            <Text code style={{ fontSize: 16 }}>{analysisIPData?.ip}</Text>
          </div>
        }
        open={analysisModalVisible}
        onCancel={() => {
          setAnalysisModalVisible(false);
          setAnalysisIPData(null);
          setAttackFrequencyStats(null);
          setDelayPrediction(null);
          setDelayCalculationHistory(null);
        }}
        footer={null}
        width={1000}
        centered
      >
        {analysisIPData && (
          <Spin spinning={analysisLoading}>
            <div>
              <Alert
                message="延迟模式分析"
                description={
                  analysisIPData.config.delayMode === 'auto' ? 
                  '当前使用自动延迟模式，系统将根据攻击频率动态调整延迟时间以优化防护效果。' :
                  '当前使用手动延迟模式，使用固定的延迟时间进行防护。'
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Tabs
                defaultActiveKey="frequency"
                items={[
                  {
                    key: 'frequency',
                    label: (
                      <span>
                        📊 攻击频率统计
                      </span>
                    ),
                    children: (
                      <div>
                        {attackFrequencyStats ? (
                          <div>
                            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                              <Button 
                                size="small" 
                                onClick={() => fetchAttackFrequencyStats(analysisIPData.ip)}
                              >
                                刷新数据
                              </Button>
                            </div>
                            
                            <Row gutter={[16, 16]}>
                              <Col span={8}>
                                <Card size="small" style={{ textAlign: 'center', borderColor: '#f5222d' }}>
                                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f5222d', marginBottom: 8 }}>
                                    {attackFrequencyStats.frequencyStats?.shortTerm || 0}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#666', marginBottom: 4 }}>
                                    {attackFrequencyStats.description?.shortTerm}
                                  </div>
                                  <Tag color="red" size="small">短期频率</Tag>
                                </Card>
                              </Col>
                              <Col span={8}>
                                <Card size="small" style={{ textAlign: 'center', borderColor: '#fa8c16' }}>
                                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16', marginBottom: 8 }}>
                                    {attackFrequencyStats.frequencyStats?.mediumTerm || 0}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#666', marginBottom: 4 }}>
                                    {attackFrequencyStats.description?.mediumTerm}
                                  </div>
                                  <Tag color="orange" size="small">中期频率</Tag>
                                </Card>
                              </Col>
                              <Col span={8}>
                                <Card size="small" style={{ textAlign: 'center', borderColor: '#1890ff' }}>
                                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: 8 }}>
                                    {attackFrequencyStats.frequencyStats?.longTerm || 0}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#666', marginBottom: 4 }}>
                                    {attackFrequencyStats.description?.longTerm}
                                  </div>
                                  <Tag color="blue" size="small">长期频率</Tag>
                                </Card>
                              </Col>
                            </Row>
                            
                            <Card size="small" title="频率阈值参考" style={{ marginTop: 16 }}>
                              <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.8 }}>
                                <div style={{ marginBottom: 4 }}>
                                  <Tag color="red" size="small">高频</Tag> {attackFrequencyStats.thresholds?.high}
                                </div>
                                <div style={{ marginBottom: 4 }}>
                                  <Tag color="orange" size="small">中频</Tag> {attackFrequencyStats.thresholds?.medium}
                                </div>
                                <div>
                                  <Tag color="green" size="small">低频</Tag> {attackFrequencyStats.thresholds?.low}
                                </div>
                              </div>
                            </Card>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', color: '#999', padding: '60px 0' }}>
                            <div style={{ fontSize: '48px', marginBottom: 16 }}>📊</div>
                            <div style={{ fontSize: '16px', marginBottom: 8 }}>正在加载攻击频率统计</div>
                            <Text type="secondary">系统正在分析该IP的攻击频率数据</Text>
                          </div>
                        )}
                    </div>
                  )
                },
                {
                    key: 'calculation',
                  label: (
                    <span>
                        ⚙️ 延迟计算信息
                    </span>
                  ),
                  children: (
                      <div>
                        {delayCalculationHistory ? (
                          <div>
                            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                              <Button 
                                size="small" 
                                onClick={() => fetchDelayCalculationHistory(analysisIPData.ip)}
                              >
                                刷新数据
                              </Button>
                            </div>
                            
                            <Row gutter={16}>
                              <Col span={12}>
                                <Card size="small" title="当前配置">
                                  <div style={{ marginBottom: 12 }}>
                                    <strong>延迟模式:</strong> 
                                    <Tag color={delayCalculationHistory.currentConfig.delayMode === 'auto' ? 'cyan' : 'blue'} style={{ marginLeft: 8 }}>
                                      {delayCalculationHistory.modeDescription}
                                    </Tag>
                                  </div>
                                  {delayCalculationHistory.currentConfig.delayMode === 'auto' && (
                                    <>
                                      <div style={{ marginBottom: 8 }}>
                                        <strong>基础延迟:</strong> 
                                        <span style={{ marginLeft: 8 }}>
                                          {Math.floor((delayCalculationHistory.currentConfig.baseDelay || 300) / 60)}分钟
                                        </span>
                                      </div>
                                      {delayCalculationHistory.currentConfig.currentCalculatedDelay && (
                                        <div style={{ marginBottom: 8 }}>
                                          <strong>当前计算延迟:</strong> 
                                          <span style={{ color: '#1890ff', fontWeight: 'bold', marginLeft: 8 }}>
                                            {Math.floor(delayCalculationHistory.currentConfig.currentCalculatedDelay / 60)}分钟
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {delayCalculationHistory.currentConfig.delayMode === 'manual' && (
                                    <div style={{ marginBottom: 8 }}>
                                      <strong>固定延迟:</strong> 
                                      <span style={{ marginLeft: 8 }}>
                                        {Math.floor((delayCalculationHistory.currentConfig.attackEndDelay || 300) / 60)}分钟
                                      </span>
                                    </div>
                                  )}
                                </Card>
                              </Col>
                                                              <Col span={12}>
                                 <Card size="small" title="最新计算数据">
                                   {delayCalculationHistory.latestCalculation ? (
                                     <div>
                                       {delayCalculationHistory.latestCalculation.baseDelay && (
                                         <div style={{ marginBottom: 8 }}>
                                           <strong>基础延迟:</strong> 
                                           <span style={{ marginLeft: 8 }}>
                                             {Math.floor(delayCalculationHistory.latestCalculation.baseDelay / 60)}分钟
                                           </span>
                                         </div>
                                       )}
                                       
                                       {delayCalculationHistory.latestCalculation.calculatedDelay && (
                                         <div style={{ marginBottom: 8 }}>
                                           <strong>计算延迟:</strong> 
                                           <span style={{ color: '#1890ff', fontWeight: 'bold', marginLeft: 8 }}>
                                             {Math.floor(delayCalculationHistory.latestCalculation.calculatedDelay / 60)}分钟
                                           </span>
                                         </div>
                                       )}
                                       
                                       {delayCalculationHistory.latestCalculation.adjustmentFactor && (
                                         <div style={{ marginBottom: 8 }}>
                                           <strong>调整系数:</strong> 
                                           <span style={{ color: '#fa8c16', fontWeight: 'bold', marginLeft: 8 }}>
                                             {delayCalculationHistory.latestCalculation.adjustmentFactor}x
                                           </span>
                                         </div>
                                       )}
                                       
                                       {delayCalculationHistory.latestCalculation.adjustmentReason && (
                                         <div style={{ marginBottom: 12 }}>
                                           <strong>调整原因:</strong>
                                           <div style={{ 
                                             marginTop: 4, 
                                             padding: '8px', 
                                             background: '#f6f8fa', 
                                             borderRadius: '4px',
                                             fontSize: '12px',
                                             color: '#666',
                                             lineHeight: 1.4
                                           }}>
                                             {delayCalculationHistory.latestCalculation.adjustmentReason}
                                           </div>
                                         </div>
                                       )}
                                       
                                       {delayCalculationHistory.latestCalculation.attackStats && (
                                         <div style={{ marginBottom: 8 }}>
                                           <strong>攻击统计:</strong>
                                           <div style={{ 
                                             marginTop: 4, 
                                             padding: '6px 8px', 
                                             background: '#f0f2f5', 
                                             borderRadius: '4px',
                                             fontSize: '11px',
                                             color: '#666'
                                           }}>
                                             短期: {delayCalculationHistory.latestCalculation.attackStats.shortTerm}次/小时 | 
                                             中期: {delayCalculationHistory.latestCalculation.attackStats.mediumTerm}次/小时 | 
                                             长期: {delayCalculationHistory.latestCalculation.attackStats.longTerm}次/小时
                                           </div>
                                         </div>
                                       )}
                                       
                                       {delayCalculationHistory.latestCalculation.calculatedAt && (
                                         <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                                           <div style={{ fontSize: '11px', color: '#999' }}>
                                             <strong>计算时间:</strong> 
                                             <span style={{ marginLeft: 8 }}>
                                               {delayCalculationHistory.latestCalculation.calculatedAtFormatted || 
                                                new Date(delayCalculationHistory.latestCalculation.calculatedAt).toLocaleString('zh-CN')}
                                             </span>
                                           </div>
                                         </div>
                                       )}
                                     </div>
                                   ) : (
                                     <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                                       <Text type="secondary">暂无计算数据</Text>
                                     </div>
                                   )}
                                 </Card>
                                </Col>
                            </Row>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', color: '#999', padding: '60px 0' }}>
                            <div style={{ fontSize: '48px', marginBottom: 16 }}>⚙️</div>
                            <div style={{ fontSize: '16px', marginBottom: 8 }}>正在加载延迟计算信息</div>
                            <Text type="secondary">系统正在获取延迟计算历史数据</Text>
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    key: 'prediction',
                    label: (
                      <span>
                        🔮 延迟时间预测
                      </span>
                    ),
                    children: (
                      <div>
                        <div style={{ marginBottom: 16, textAlign: 'right' }}>
                          <Button 
                            type="primary"
                            onClick={() => predictDelayTime(analysisIPData.ip, analysisIPData.config.baseDelay || 300)}
                          >
                            执行预测
                          </Button>
                        </div>
                        
                        {delayPrediction ? (
                          <div>
                            <div style={{ marginBottom: 16 }}>
                              <Tag color="blue" icon="🕒">
                                预测时间: {delayPrediction.calculatedAtFormatted}
                              </Tag>
                            </div>

                            <Row gutter={16}>
                              <Col span={12}>
                                <Card 
                                  size="small" 
                                  title="📊 延迟预测结果"
                                  style={{ 
                                    background: '#f6ffed', 
                                    border: '1px solid #b7eb8f'
                                  }}
                                >
                                  {delayPrediction.prediction && (
                                    <div>
                                      <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#666' }}>基础延迟:</span>
                                        <span style={{ fontWeight: 'bold', marginLeft: 8, color: '#52c41a' }}>
                                          {Math.floor((delayPrediction.prediction.baseDelay || 0) / 60)}分钟
                                        </span>
                                      </div>
                                      
                                      <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#666' }}>计算延迟:</span>
                                        <span style={{ fontWeight: 'bold', marginLeft: 8, color: '#52c41a' }}>
                                          {Math.floor((delayPrediction.prediction.calculatedDelay || 0) / 60)}分钟
                                        </span>
                                      </div>
                                      
                                      <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#666' }}>调整系数:</span>
                                        <span style={{ fontWeight: 'bold', marginLeft: 8, color: '#fa8c16' }}>
                                          {delayPrediction.prediction.adjustmentFactor}x
                                        </span>
                                      </div>
                                      
                                      {delayPrediction.prediction.delayChangeFormatted && (
                                        <div style={{ marginBottom: 8 }}>
                                          <span style={{ color: '#666' }}>延迟变化:</span>
                                          <Tag color="orange" style={{ marginLeft: 8 }}>
                                            {delayPrediction.prediction.delayChangeFormatted}
                                          </Tag>
                                        </div>
                                      )}
                                      
                                      {delayPrediction.prediction.adjustmentReason && (
                                        <div style={{ 
                                          marginTop: 12, 
                                          padding: '8px', 
                                          background: '#fff', 
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          color: '#666'
                                        }}>
                                          <strong>调整原因:</strong><br/>
                                          {delayPrediction.prediction.adjustmentReason}
                                        </div>
                                      )}
                                      
                                      {delayPrediction.prediction.attackStats && (
                                        <div style={{ 
                                          marginTop: 12, 
                                          padding: '8px', 
                                          background: '#fff', 
                                          borderRadius: '4px'
                                        }}>
                                          <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                                            攻击频率统计:
                                          </div>
                                          <div style={{ fontSize: '11px', color: '#666' }}>
                                            短期: {delayPrediction.prediction.attackStats.shortTerm}次/小时 | 
                                            中期: {delayPrediction.prediction.attackStats.mediumTerm}次/小时 | 
                                            长期: {delayPrediction.prediction.attackStats.longTerm}次/小时
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Card>
                              </Col>
                              
                              <Col span={12}>
                                {delayPrediction.recommendation && (
                                  <Card 
                                    size="small" 
                                    title="💡 系统建议"
                                    style={{ 
                                      background: '#fff7e6', 
                                      border: '1px solid #ffd591'
                                    }}
                                  >
                                    <div style={{ marginBottom: 12 }}>
                                      <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#666' }}>推荐模式:</span>
                                        <Tag color={
                                          delayPrediction.recommendation.recommendedMode === 'aggressive' ? 'red' :
                                          delayPrediction.recommendation.recommendedMode === 'moderate' ? 'orange' :
                                          'green'
                                        } style={{ marginLeft: 8 }}>
                                          {delayPrediction.recommendation.recommendedMode === 'aggressive' ? '激进模式' :
                                           delayPrediction.recommendation.recommendedMode === 'moderate' ? '适中模式' :
                                           '保守模式'}
                                        </Tag>
                                      </div>
                                      
                                      {delayPrediction.recommendation.suggestedBaseDelay && (
                                        <div style={{ marginBottom: 8 }}>
                                          <span style={{ color: '#666' }}>建议基础延迟:</span>
                                          <span style={{ fontWeight: 'bold', marginLeft: 8, color: '#fa8c16' }}>
                                            {Math.floor(delayPrediction.recommendation.suggestedBaseDelay / 60)}分钟
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {delayPrediction.recommendation.description && (
                                      <div style={{ 
                                        padding: '10px', 
                                        background: '#fff', 
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        color: '#666',
                                        lineHeight: 1.5
                                      }}>
                                        <strong>详细说明:</strong><br/>
                                        {delayPrediction.recommendation.description}
                                      </div>
                                    )}
                                    
                                    {delayPrediction.recommendation.attackStats && (
                                      <div style={{ 
                                        marginTop: 12, 
                                        padding: '8px', 
                                        background: '#fff', 
                                        borderRadius: '4px'
                                      }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                                          基于攻击数据:
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#666' }}>
                                          短期: {delayPrediction.recommendation.attackStats.shortTerm}次/小时 | 
                                          中期: {delayPrediction.recommendation.attackStats.mediumTerm}次/小时 | 
                                          长期: {delayPrediction.recommendation.attackStats.longTerm}次/小时
                                        </div>
                                      </div>
                                    )}
                                  </Card>
                                )}
                              </Col>
                            </Row>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', color: '#999', padding: '60px 0' }}>
                            <div style={{ fontSize: '48px', marginBottom: 16 }}>🔮</div>
                            <div style={{ fontSize: '16px', marginBottom: 8 }}>智能延迟预测</div>
                            <Text type="secondary">点击"执行预测"按钮获取基于历史攻击数据的延迟时间预测结果</Text>
                          </div>
                        )}
                      </div>
                    )
                  }
                ]}
              />

              {/* 建议和说明 */}
              {analysisIPData.config.delayMode === 'manual' && (
                <Alert
                  message="建议启用自动延迟模式"
                  description="如果该IP经常遭受攻击，建议考虑使用自动延迟模式，系统将根据攻击频率智能调整延迟时间，提供更优的防护效果。"
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </div>
          </Spin>
        )}
      </Modal>

      {/* 操作日志模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined />
            <span>操作日志 - </span>
            <Text code style={{ fontSize: 16 }}>{logsIPData?.ip}</Text>
          </div>
        }
        open={logsModalVisible}
        onCancel={() => {
          setLogsModalVisible(false);
          setLogsIPData(null);
          setOperationLogs([]);
        }}
        footer={null}
        width={1000}
        centered
        style={{ maxHeight: '85vh' }}
        bodyStyle={{ maxHeight: '75vh', overflowY: 'auto', padding: '16px' }}
      >
        {logsIPData && (
                    <Spin spinning={logsLoading}>
                      {operationLogs.length > 0 ? (
                        <div>
                <div style={{ marginBottom: 16, padding: '12px 16px', backgroundColor: '#f6f8fa', borderRadius: 6, fontSize: 13, color: '#666' }}>
                  <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 'bold', color: '#333' }}>
                    📊 操作日志统计 - 显示最近 {operationLogs.length} 条记录
                          </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {(() => {
                      const categoryStats = operationLogs.reduce((stats, log) => {
                        if (log && log.action) {
                          const typeInfo = getLogTypeInfo(log.action);
                          stats[typeInfo.category] = (stats[typeInfo.category] || 0) + 1;
                        }
                        return stats;
                      }, {});
                      
                      const categoryNames = {
                        attack: '攻击检测',
                        protection: '防护策略',
                        udp: 'UDP策略',
                        overseas: '海外策略',
                        user: '用户操作',
                        admin: '管理员操作',
                        unknown: '其他'
                      };
                      
                      return Object.entries(categoryStats).map(([category, count]) => (
                        <Tag key={category} color={
                          category === 'attack' ? 'volcano' :
                          category === 'protection' ? 'blue' :
                          category === 'udp' ? 'cyan' :
                          category === 'overseas' ? 'geekblue' :
                          category === 'user' ? 'green' :
                          category === 'admin' ? 'purple' :
                          'default'
                        }>
                          {categoryNames[category] || category}: {count}
                        </Tag>
                      ));
                    })()}
                  </div>
                </div>
                
                <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
                          {operationLogs.map((log, index) => {
                            // 确保log是一个对象，如果不是则跳过
                            if (!log || typeof log !== 'object' || !log.action) {
                              return null;
                            }
                    
                    const typeInfo = getLogTypeInfo(log.action);
                            
                            return (
                              <div key={index} style={{ 
                        padding: '12px 16px', 
                        borderBottom: index < operationLogs.length - 1 ? '1px solid #f0f0f0' : 'none',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                        borderLeft: `4px solid ${
                          typeInfo.severity === 'high' ? '#f5222d' :
                          typeInfo.severity === 'medium' ? '#fa8c16' :
                          '#d9d9d9'
                        }`
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '18px' }}>{typeInfo.icon}</span>
                              <Tag 
                                color={
                                  typeInfo.severity === 'high' ? 'red' :
                                  typeInfo.severity === 'medium' ? 'orange' :
                                  typeInfo.category === 'attack' ? 'volcano' :
                                  typeInfo.category === 'protection' ? 'blue' :
                                  typeInfo.category === 'udp' ? 'cyan' :
                                  typeInfo.category === 'overseas' ? 'geekblue' :
                                  typeInfo.category === 'user' ? 'green' :
                                  typeInfo.category === 'admin' ? 'purple' :
                                  'default'
                                }
                                style={{ fontWeight: 500 }}
                              >
                                {typeInfo.text}
                                      </Tag>
                              {typeInfo.severity === 'high' && (
                                <Badge status="error" text="高风险" style={{ fontSize: '12px' }} />
                              )}
                              {typeInfo.severity === 'medium' && (
                                <Badge status="warning" text="中风险" style={{ fontSize: '12px' }} />
                              )}
                            </div>
                            <div style={{ 
                              color: '#333', 
                              fontWeight: 500, 
                              marginBottom: 8,
                              fontSize: '14px',
                              lineHeight: 1.5
                            }}>
                                        {log.description}
                                    </div>
                                    
                                    {/* 处理不同类型的详情信息 */}
                            {log.details && (() => {
                              // 检查是否有实际的详情内容需要显示
                              const hasDetails = (
                                (log.details.changes && Array.isArray(log.details.changes)) ||
                                log.details.addedBy ||
                                (log.details.removedStrategies && Array.isArray(log.details.removedStrategies)) ||
                                log.details.strategyType ||
                                log.details.ongoingAttacks ||
                                log.details.delaySeconds ||
                                log.details.scheduledRemovalTime ||
                                log.details.reason ||
                                log.details.removeType ||
                                log.details.result ||
                                log.details.initialConfig ||
                                log.details.previousConfig ||
                                log.details.username ||
                                log.details.updatedBy ||
                                log.details.checkResult ||
                                (log.details.configChanges && Array.isArray(log.details.configChanges))
                              );

                              if (!hasDetails) return null;

                              return (
                                <div style={{ color: '#666', fontSize: '12px', lineHeight: 1.5, background: '#f8f9fa', padding: '8px 12px', borderRadius: 4 }}>
                                        {log.details.changes && Array.isArray(log.details.changes) && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>变更内容:</strong> {log.details.changes.join(', ')}
                                          </div>
                                        )}

                                        {log.details.addedBy && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>添加人:</strong> {log.details.addedBy}
                                          </div>
                                        )}
                                        {log.details.removedStrategies && Array.isArray(log.details.removedStrategies) && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>移除策略:</strong> {log.details.removedStrategies.join(', ')}
                                          </div>
                                        )}
                                        {log.details.strategyType && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>策略类型:</strong> {log.details.strategyType}
                                          </div>
                                        )}
                                        {log.details.ongoingAttacks && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>进行中攻击:</strong> {log.details.ongoingAttacks}
                                          </div>
                                        )}
                                        {log.details.delaySeconds && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>延迟时间:</strong> {log.details.delaySeconds}秒
                                          </div>
                                        )}
                                        {log.details.scheduledRemovalTime && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>计划移除时间:</strong> {log.details.scheduledRemovalTime}
                                          </div>
                                        )}
                                        {log.details.reason && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>原因:</strong> {log.details.reason}
                                          </div>
                                        )}
                                        {log.details.removeType && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>移除类型:</strong> {log.details.removeType === 'automatic' ? '自动' : '手动'}
                                          </div>
                                        )}
                                        {log.details.result && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>结果:</strong> 
                                            <Tag color={log.details.result === 'success' ? 'green' : 'red'} size="small" style={{ marginLeft: 4 }}>
                                              {log.details.result === 'success' ? '成功' : '失败'}
                                            </Tag>
                                          </div>
                                        )}
                                        {log.details.initialConfig && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>初始配置:</strong>
                                            <div style={{ marginLeft: 16, marginTop: 4 }}>
                                              UDP封禁: {log.details.initialConfig.enableUdpBlock ? '启用' : '禁用'}, 
                                              海外封禁: {log.details.initialConfig.enableOverseasBlock ? '启用' : '禁用'}, 
                                              延迟: {log.details.initialConfig.attackEndDelay}秒
                                            </div>
                                          </div>
                                        )}
                                        {log.details.previousConfig && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>之前配置:</strong>
                                            <div style={{ marginLeft: 16, marginTop: 4 }}>
                                              UDP封禁: {log.details.previousConfig.enableUdpBlock ? '启用' : '禁用'}, 
                                              海外封禁: {log.details.previousConfig.enableOverseasBlock ? '启用' : '禁用'}, 
                                              延迟: {log.details.previousConfig.attackEndDelay}秒
                                            </div>
                                          </div>
                                        )}
                                        {(log.details.username || log.details.updatedBy) && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>操作人:</strong> {log.details.username || log.details.updatedBy}
                                          </div>
                                        )}
                                        {log.details.checkResult && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>检查结果:</strong> {log.details.checkResult}
                                          </div>
                                        )}
                                        {log.details.configChanges && Array.isArray(log.details.configChanges) && (
                                          <div style={{ marginBottom: 4 }}>
                                            <strong>配置变更:</strong> {log.details.configChanges.join(', ')}
                                          </div>
                                        )}
                                      </div>
                              );
                            })()}
                                  </div>
                          <div style={{ 
                            color: '#999', 
                            fontSize: '12px', 
                            whiteSpace: 'nowrap', 
                            marginLeft: 16,
                            alignSelf: 'flex-start',
                            marginTop: 4
                          }}>
                                    {log.datetime || (log.timestamp ? new Date(log.timestamp).toLocaleString('zh-CN') : '未知时间')}
                                  </div>
                                </div>
                              </div>
                                                         );
                           })}
                          </div>
                        </div>
                        ) : (
              <div style={{ textAlign: 'center', color: '#999', padding: '60px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: 16 }}>📝</div>
                <div style={{ fontSize: '16px', marginBottom: 8 }}>暂无操作日志</div>
                <Text type="secondary">该IP还没有任何操作记录</Text>
                          </div>
                        )}
                      </Spin>
        )}
      </Modal>

    </div>
  );
};

export default AdminAutoProtection; 